import { LineItem } from 'shared/types/cart';
import { groupLineItemsBySku, separateMediaAndPrintBuilderProducts } from '../../cart/helpers';
import { DeliveryResponse, GroupedLineItemsResult } from '../types';

export const timelineLastStatus = ['DELIVERED', 'PAID'];

// Possible handled status with flow
export const POSSIBLESTATUSES = [
  'OPEN',
  'INRENDERING',
  'INPRODUCTION',
  'MEDIARETRIEVED',
  'MEDIARETRIEVALFAILED',
  'ERROR',
  'SHIPPED',
  'CANCELLED',
  'CANCELREJECTED',
  'HOLD',
  'REFUNDED',
  'PARTIALREFUND',
  'REMAKE',
  'DESTROYED',
  'PAID',
  'DELIVERED',
];

export function getBackendCurrentIndex(status: string, steps: { label: string }[]): number {
  return steps.map((a) => a.label.toLowerCase()).indexOf(status.toLowerCase()) ?? 0;
}

export function getP2RBackendTimelineSteps(status: string, cartKind: string): { label: string }[] {
  const baseFlow = ['OPEN', 'INRENDERING', 'MEDIARETRIEVED', 'SHIPPED'];
  const finalStep = cartKind === 'm2hCart' ? 'DELIVERED' : 'PAID';

  if (status === 'DESTROYED') {
    return ['OPEN', 'INRENDERING', 'MEDIARETRIEVED', 'DESTROYED'].map((label) => ({ label }));
  }

  if (status === 'HOLD') {
    const holdFlow = ['OPEN', 'INRENDERING', 'MEDIARETRIEVED', 'HOLD', 'SHIPPED'];
    return [...holdFlow, finalStep].map((label) => ({ label }));
  }

  // Normal flow (OPEN → PAID)
  return [...baseFlow, finalStep].map((label) => ({ label }));
}

export const ORDER_STATUS_PRIORITY = ['INRENDERING', 'MEDIARETRIEVED'] as const;
const ORDER_STATUS_EXISTENCE_PRIORITY = ['REMAKE'];

export const ORDER_STATUS_FALLBACK_MAP = {
  INRENDERING: 'OPEN',
  MEDIARETRIEVED: 'INRENDERING',
} as const;

const getStatus = (item: LineItem) => item?.custom?.fields?.statusCode?.toUpperCase();

export const findFirstExistingStatusByPriority = (items: LineItem[] = []) => {
  // Loop through priority list
  for (const targetStatus of ORDER_STATUS_EXISTENCE_PRIORITY) {
    const matchedItem = items.some((item) => getStatus(item) === targetStatus?.toUpperCase());

    if (matchedItem) {
      return targetStatus;
    }
  }

  // No status matched
  return undefined;
};

export function groupLineItemsByOrderStatus(items?: LineItem[]): GroupedLineItemsResult[] {
  if (!items || items.length === 0) return [];

  const groups: Record<string, LineItem[]> = items.reduce(
    (acc, item) => {
      let status = item.custom?.fields?.statusCode?.toUpperCase() ?? 'INRENDERING';

      status = normalizeStatus(status);

      if (!acc[status]) {
        acc[status] = [];
      }

      acc[status].push(item);
      return acc;
    },
    {} as Record<string, LineItem[]>,
  );

  return Object.entries(groups).map(([statusCode, lineItems]) => ({
    statusCode,
    lineItems: getFormattedLineItems(lineItems),
    trackingDetails: undefined,
  }));
}

// grouping by tracking id

export function groupLineItemsWithTrackingAndStatusSplit(
  lineItems: LineItem[] = [],
  deliveries: DeliveryResponse = [],
): GroupedLineItemsResult[] {
  if (!lineItems.length) return [];

  const { shippedGroups, shippedQtyMap } = buildM2HShippedGroups(lineItems, deliveries);

  const remainingGroups = buildM2HRemainingGroups(lineItems, shippedQtyMap);

  return [...shippedGroups, ...remainingGroups];
}

function isShipped(item?: LineItem): boolean {
  return item?.custom?.fields?.statusCode?.toUpperCase() === 'SHIPPED';
}

function collectTrackedShippedItems(
  deliveries: DeliveryResponse,
  lineItemMap: Map<string, LineItem>,
  trackingGroups: Record<string, GroupedLineItemsResult>,
  shippedQtyMap: Map<string, number>,
) {
  for (const delivery of deliveries) {
    for (const parcel of delivery.parcels ?? []) {
      const { trackingId, carrier } = parcel.trackingData ?? {};
      if (!trackingId) continue;

      for (const parcelItem of parcel.items ?? []) {
        const original = lineItemMap.get(parcelItem.id);
        if (!original || !isShipped(original)) continue;

        trackingGroups[trackingId] ||= {
          statusCode: 'SHIPPED',
          trackingDetails: { trackingId, carrier },
          lineItems: [],
        };

        shippedQtyMap.set(parcelItem.id, (shippedQtyMap.get(parcelItem.id) ?? 0) + parcelItem.quantity);

        trackingGroups[trackingId].lineItems.push({
          ...original,
          count: parcelItem.quantity,
        });
      }
    }
  }
}

function collectUntrackedShippedItems(lineItems: LineItem[], shippedQtyMap: Map<string, number>): LineItem[] {
  const result: LineItem[] = [];

  for (const item of lineItems) {
    if (!isShipped(item)) continue;

    const shippedQty = shippedQtyMap.get(item.lineItemId!) ?? 0;

    const remainingQty = (item.count ?? 0) - shippedQty;
    if (remainingQty <= 0) continue;

    result.push({ ...item, count: remainingQty });
  }

  return result;
}

function buildM2HShippedGroups(
  lineItems: LineItem[],
  deliveries: DeliveryResponse,
): {
  shippedGroups: GroupedLineItemsResult[];
  shippedQtyMap: Map<string, number>;
} {
  const lineItemMap = new Map(lineItems.map((li) => [li.lineItemId!, li]));

  const shippedQtyMap = new Map<string, number>();
  const trackingGroups: Record<string, GroupedLineItemsResult> = {};

  collectTrackedShippedItems(deliveries, lineItemMap, trackingGroups, shippedQtyMap);

  const untrackedShippedItems = collectUntrackedShippedItems(lineItems, shippedQtyMap);

  const shippedGroups: GroupedLineItemsResult[] = [
    ...Object.values(trackingGroups).map((g) => ({
      ...g,
      lineItems: getFormattedLineItems(g.lineItems),
    })),
  ];

  if (untrackedShippedItems.length) {
    shippedGroups.push({
      statusCode: 'SHIPPED',
      trackingDetails: undefined,
      lineItems: getFormattedLineItems(untrackedShippedItems),
    });
  }

  return { shippedQtyMap, shippedGroups };
}

function buildM2HRemainingGroups(lineItems: LineItem[], shippedQtyMap: Map<string, number>): GroupedLineItemsResult[] {
  const groups: Record<string, LineItem[]> = {};

  for (const item of lineItems) {
    let status = item.custom?.fields?.statusCode?.toUpperCase() ?? 'OPEN';

    status = normalizeStatus(status);

    // ✅ Only SHIPPED items participate in quantity subtraction
    const isShippedStatus = status === 'SHIPPED';

    const shippedQty = isShippedStatus ? (shippedQtyMap.get(item.lineItemId!) ?? 0) : 0;

    const remainingQty = (item.count ?? 0) - shippedQty;

    if (remainingQty <= 0) continue;

    groups[status] ||= [];
    groups[status].push({ ...item, count: remainingQty });
  }

  // ✅ EXACTLY like your P2R reducer, but reused for M2H
 return Object.entries(groups)
    .filter(([status]) => status !== 'SHIPPED') // shipped already handled
    .map(([statusCode, items]) => {
      return ({
      statusCode,
      lineItems:getFormattedLineItems(items),
      trackingDetails: undefined,
    })});
}

export function getTotalCount(items: LineItem[]): number {
  return items.reduce((sum, item) => sum + (item.count ?? 0), 0);
}

export function hasSameStatusCode(lineItems: LineItem[]) {
  if (!Array.isArray(lineItems) || lineItems.length === 0) {
    return false;
  }

  const firstStatus = lineItems[0]?.custom?.fields?.statusCode?.toUpperCase();

  if (!firstStatus) {
    return false;
  }

  return lineItems.every((item) => item?.custom?.fields?.statusCode?.toUpperCase() === firstStatus);
}

export function sortByStatusFlow(data: GroupedLineItemsResult[], statusFlow: string[]): GroupedLineItemsResult[] {
  const orderMap = new Map<string, number>(statusFlow.map((status, index) => [status, index]));

  return [...data].sort((a, b) => {
    const orderA = orderMap.get(a.statusCode);
    const orderB = orderMap.get(b.statusCode);

    // Unknown statuses go to the end
    if (orderA === undefined && orderB === undefined) return 0;
    if (orderA === undefined) return 1;
    if (orderB === undefined) return -1;

    return orderA - orderB;
  });
}

function normalizeStatus(status: string): string {
  if (status === 'INPRODUCTION' || status === 'MEDIARETRIEVALFAILED' || status === 'RENDERED') {
    return 'INRENDERING';
  }

  if (status === 'CANCELREJECTED') {
    return 'CANCELLED';
  }

  if (status === 'ERROR' || status === 'PRINTED') {
    return 'MEDIARETRIEVED';
  }

  if (!POSSIBLESTATUSES.includes(status)) {
    return 'INRENDERING';
  }

  // Unknown status → return original
  return status;
}

const getFormattedLineItems = (items: LineItem[]) => {
  const [mediaClipLineItems, printBuilderLineItems] =
    separateMediaAndPrintBuilderProducts(items ?? []);

  const formattedMediaClipLineItems = mediaClipLineItems.map((lineItem) => ({
    ...lineItem,
    reorder: {
      quantity: lineItem?.count,
      distributionChannel: lineItem?.distributionChannel,
      productSku: lineItem?.variant?.sku,
    },
  }));

  return [
    ...printBuilderLineItems.flatMap((group) => groupLineItemsBySku(group)),
    ...formattedMediaClipLineItems,
  ];
};
